import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import { theme } from '../../theme';

function CheckInOutWidget() {
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [clock, setClock] = useState(new Date());
  const [error, setError] = useState('');

  const load = useCallback(() => {
    hrms.getTodayAttendance()
      .then((r) => setAttendance(r.data))
      .catch(() => setAttendance(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live clock tick
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleCheckIn = async () => {
    setError('');
    setActionLoading('in');
    try {
      await hrms.checkIn();
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Check-in failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleCheckOut = async () => {
    setError('');
    setActionLoading('out');
    try {
      await hrms.checkOut();
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Check-out failed');
    } finally {
      setActionLoading('');
    }
  };

  const timeStr = clock.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const dateStr = clock.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  const checkedIn = attendance?.checked_in;
  const checkedOut = attendance?.checked_out;
  const statusLabel = checkedIn ? (checkedOut ? 'Checked Out' : 'Checked In') : 'Not Checked In';
  const statusColor = checkedIn ? (checkedOut ? '#6B7280' : '#10B981') : '#F59E0B';
  const source = attendance?.source;
  const sourceLabel = source === 'biometric' ? '👁 Biometric' : source === 'both' ? '✅ Biometric + Manual' : source === 'manual' ? '🖐 Manual' : null;
  const sourceBg    = source === 'biometric' ? '#EEF2FF' : source === 'both' ? '#D1FAE5' : '#FEF3C7';
  const sourceColor = source === 'biometric' ? '#4338CA' : source === 'both' ? '#065F46' : '#92400E';

  return (
    <div style={{
      background: theme.white,
      border: `1px solid ${theme.border}`,
      borderRadius: '0.75rem',
      padding: '1.25rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      flexWrap: 'wrap',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {/* Left: Clock */}
      <div>
        <p style={{ fontSize: '1.8rem', fontWeight: 700, color: theme.text, margin: 0, letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums' }}>
          {timeStr}
        </p>
        <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>{dateStr}</p>
      </div>

      {/* Middle: Today's record */}
      {!loading && (
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0 0 0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Check-In</p>
            <p style={{ fontSize: '1rem', fontWeight: 700, color: checkedIn ? '#10B981' : theme.textMuted, margin: 0 }}>
              {attendance?.check_in || '--:--'}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0 0 0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Check-Out</p>
            <p style={{ fontSize: '1rem', fontWeight: 700, color: checkedOut ? '#6B7280' : theme.textMuted, margin: 0 }}>
              {attendance?.check_out || '--:--'}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0 0 0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</p>
            <p style={{ fontSize: '0.8rem', fontWeight: 700, color: statusColor, margin: 0 }}>{statusLabel}</p>
          </div>
          {attendance?.hours_worked && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0 0 0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Hours</p>
              <p style={{ fontSize: '1rem', fontWeight: 700, color: theme.text, margin: 0 }}>{attendance.hours_worked}h</p>
            </div>
          )}
          {sourceLabel && (
            <span style={{ padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, background: sourceBg, color: sourceColor }}>
              {sourceLabel}
            </span>
          )}
        </div>
      )}

      {/* Right: Buttons */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        {error && <p style={{ color: theme.danger, fontSize: '0.78rem', margin: 0 }}>{error}</p>}

        {/* Biometric notice — no manual allowed when biometric is recorded */}
        {!loading && !checkedIn && source === 'biometric' && (
          <p style={{ fontSize: '0.78rem', color: '#4338CA', background: '#EEF2FF', padding: '0.4rem 0.75rem', borderRadius: '0.375rem', margin: 0, border: '1px solid #C7D2FE' }}>
            Biometric attendance recorded — manual check-in not allowed.
          </p>
        )}

        {/* Check-In button: hidden once checked in OR if biometric already recorded */}
        {!loading && !checkedIn && source !== 'biometric' && (
          <button
            onClick={handleCheckIn}
            disabled={actionLoading === 'in'}
            style={{
              padding: '0.6rem 1.25rem',
              background: actionLoading === 'in' ? '#A7F3D0' : '#10B981',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: actionLoading === 'in' ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {actionLoading === 'in' ? 'Checking In...' : '✓ Check In'}
          </button>
        )}

        {/* Check-Out button: shown once checked in, always re-clickable */}
        {!loading && checkedIn && (
          <button
            onClick={handleCheckOut}
            disabled={actionLoading === 'out'}
            style={{
              padding: '0.6rem 1.25rem',
              background: actionLoading === 'out' ? '#BAE6FD' : '#3B82F6',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              fontSize: '0.875rem',
              cursor: actionLoading === 'out' ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {actionLoading === 'out' ? 'Checking Out...' : '→ Check Out'}
          </button>
        )}
      </div>
    </div>
  );
}

export default CheckInOutWidget;
